import { Button, Heading, HFlow, Icon, Modal, ModalBody, ModalFooter, Text, VFlow } from 'bold-ui'
import { useErrorHandler } from 'components/error'
import clearTypename from 'graphql/clearTypename'
import {
  useBuscaCidadaoAtualizadoForcadoSubscription,
  useForcaAtualizacaoCidadaoCadsusLazyQuery,
  useSincronizaCidadaoCadsusPollingQuery,
} from 'graphql/hooks.generated'
import { SincronizacaoCadsusResultEnum } from 'graphql/types.generated'
import React, { useState } from 'react'
import { useCallback } from 'react'
import { useEffect } from 'react'
import { v4 as uuidv4 } from 'uuid'

import CidadaoFormModel from '../types/CidadaoFormModel'
import { convertToForm } from '../types/convertModelToInput'
import { ModalBuscaInformacoesCadsus, ModalBuscaInformacoesContentState } from './ModalBuscaInformacoesCadsus'

interface SincronizarCadsusButtonProps {
  cns: string
  cidadaoId: string
  setInfoCidadaoState(value: React.SetStateAction<CidadaoFormModel>): void
  setModalSincronizacaoState(isModalopen: boolean): void
  setInfoCidadaoState(value: React.SetStateAction<CidadaoFormModel>): void
}

interface ModalConfirmarBuscaDadosProps {
  cns: string
}

let buscaCidadaoAtualizadoPolling = false

export function SincronizarCadsusButton(props: SincronizarCadsusButtonProps) {
  const [modalConfirmaBuscaDadosState, setModalConfirmaBuscaDadosState] = useState(false)

  const [isModalBuscaCidadaoOpenState, setModalBuscaCidadaoOpenState] = useState<boolean>(false)
  const [resultBuscaInfoCidadaoState, setResultBuscaInfoCidadaoState] = useState<ModalBuscaInformacoesContentState>({
    isLoading: false,
  })
  const [uuidBuscaCidadaoAtualizado, setUuidBuscaCidadaoAtualizado] = useState<string>()

  const [queryForcaAtualizacaoCidadaoCadsus] = useForcaAtualizacaoCidadaoCadsusLazyQuery()

  const handleRejection = useErrorHandler()

  const atualizaResultadoBuscacidadao = useCallback(
    (data) => {
      buscaCidadaoAtualizadoPolling = false
      if (
        data.result === SincronizacaoCadsusResultEnum.SUCESSO ||
        data.result === SincronizacaoCadsusResultEnum.ERRO_CADSUS_DESABILITADO ||
        data.result === SincronizacaoCadsusResultEnum.ERRO_SEM_CONEXAO
      ) {
        setModalBuscaCidadaoOpenState(false)
      } else {
        setResultBuscaInfoCidadaoState({
          cns: data.cidadaoCadsus?.cns,
          cpf: data.cidadaoCadsus?.cpf,
          errors: data.errors,
          statusSincronizacao: data.result,
          isLoading: false,
        })
      }
      props.setInfoCidadaoState(convertToForm(clearTypename(data.cidadaoLocal)))
    },
    [props]
  )

  const { refetch: buscaCadsusPollingRefetch } = useSincronizaCidadaoCadsusPollingQuery({ skip: true })

  useEffect(() => {
    let shouldUpdateBuscaCidadaoAtualizado = true

    const scheduleCadsusPolling = setInterval(() => {
      if (!!uuidBuscaCidadaoAtualizado && buscaCidadaoAtualizadoPolling && shouldUpdateBuscaCidadaoAtualizado) {
        shouldUpdateBuscaCidadaoAtualizado = false
        buscaCadsusPollingRefetch({ uuid: uuidBuscaCidadaoAtualizado })
          .then(
            ({ data }) =>
              !!data.sincronizacaoCadsusPolling &&
              data.sincronizacaoCadsusPolling.uuid === uuidBuscaCidadaoAtualizado &&
              buscaCidadaoAtualizadoPolling &&
              atualizaResultadoBuscacidadao(data.sincronizacaoCadsusPolling)
          )
          .finally(() => (shouldUpdateBuscaCidadaoAtualizado = true))
          .catch(handleRejection)
      }
    }, 1 * 1000)

    return () => clearInterval(scheduleCadsusPolling)
  }, [atualizaResultadoBuscacidadao, buscaCadsusPollingRefetch, handleRejection, uuidBuscaCidadaoAtualizado])

  useBuscaCidadaoAtualizadoForcadoSubscription({
    onSubscriptionData: (options) => {
      const data = options.subscriptionData.data.buscaCidadaoCadsusAtualizadoForcadoCompleta
      if (uuidBuscaCidadaoAtualizado === data.uuid && buscaCidadaoAtualizadoPolling) {
        atualizaResultadoBuscacidadao(data)
      }
    },
  })

  const handleOnSincronizarClicked = () => {
    setModalConfirmaBuscaDadosState(true)
  }

  const handleOnCloseModal = () => {
    setModalConfirmaBuscaDadosState(false)
  }

  const handleOnContinuarClicked = () => {
    const hash = uuidv4()
    buscaCidadaoAtualizadoPolling = true
    queryForcaAtualizacaoCidadaoCadsus({
      variables: { input: { cidadao: props.cidadaoId, uuid: hash } },
    })
    setUuidBuscaCidadaoAtualizado(hash)
    setResultBuscaInfoCidadaoState({ ...resultBuscaInfoCidadaoState, isLoading: true })
    setModalConfirmaBuscaDadosState(false)
    setModalBuscaCidadaoOpenState(true)
  }

  return (
    <>
      <Button onClick={handleOnSincronizarClicked} kind='primary'>
        <Icon icon='sync' />
        Sincronizar com Cadsus
      </Button>

      <Modal open={modalConfirmaBuscaDadosState} onClose={handleOnCloseModal} size='small'>
        <ConfirmarBuscaDadosModalBody cns={props.cns} />
        <ModalFooter>
          <HFlow justifyContent='flex-end'>
            <Button onClick={handleOnCloseModal}>Cancelar</Button>
            <Button onClick={handleOnContinuarClicked} kind='primary'>
              Continuar
            </Button>
          </HFlow>
        </ModalFooter>
      </Modal>

      <ModalBuscaInformacoesCadsus
        result={resultBuscaInfoCidadaoState}
        isModalOpen={isModalBuscaCidadaoOpenState}
        setModalState={setModalBuscaCidadaoOpenState}
        isLoading={resultBuscaInfoCidadaoState.isLoading}
      />
    </>
  )
}

function ConfirmarBuscaDadosModalBody(props: ModalConfirmarBuscaDadosProps) {
  return (
    <ModalBody>
      <VFlow>
        <HFlow>
          <Icon icon='questionMarkFilled' fill='info' size={4} />
          <Heading level={2}>Deseja sincronizar com o CADSUS?</Heading>
        </HFlow>
        <Text>
          Os dados do cadastro de CNS {props.cns} no CADSUS serão carregados para esta tela. As alterações só serão
          efetivadas após salvar o cadastro.
        </Text>
      </VFlow>
    </ModalBody>
  )
}
